/**
 * 数字人绿幕抠像：把腾讯云渲染视频里的绿幕抠成透明，露出容器浅灰底。
 * 优先 canvas 逐帧抠像素；画面跨域读不到时回退到 SVG filter（不依赖读像素）。
 */

const FILTER_ID = 'dh-chroma-key-filter'
const CANVAS_CLASS = 'dh-chroma-canvas'

export function ensureSvgFilter() {
  if (typeof document === 'undefined') return
  if (document.getElementById(FILTER_ID)) return

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('aria-hidden', 'true')
  svg.setAttribute('width', '0')
  svg.setAttribute('height', '0')
  svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden'
  svg.innerHTML = `
    <filter id="${FILTER_ID}" color-interpolation-filters="sRGB">
      <feColorMatrix in="SourceGraphic" type="matrix" values="
         0     0     0    0 0
        -0.55  1    -0.55 0 0
         0     0     0    0 0
         0     0     0    1 0" result="greenExcess"/>
      <feComponentTransfer in="greenExcess" result="mask">
        <feFuncR type="discrete" tableValues="0"/>
        <feFuncG type="table" tableValues="0 0 0 0.05 0.25 0.6 0.9 1 1"/>
        <feFuncB type="discrete" tableValues="0"/>
        <feFuncA type="discrete" tableValues="1"/>
      </feComponentTransfer>
      <feColorMatrix in="mask" type="matrix" values="
        0 0 0 0 0
        0 0 0 0 0
        0 0 0 0 0
        0 1 0 0 0" result="alphaMask"/>
      <feComposite in="SourceGraphic" in2="alphaMask" operator="out"/>
    </filter>
  `
  document.body.appendChild(svg)
}

function findVideo(container) {
  return container?.querySelector?.('video') || null
}

function drawVideoCover(ctx, video, cw, ch, posX) {
  const vw = video.videoWidth
  const vh = video.videoHeight
  if (!vw || !vh) return
  const scale = Math.max(cw / vw, ch / vh)
  const dw = vw * scale
  const dh = vh * scale
  const dx = (cw - dw) * posX
  const dy = (ch - dh) * 0.5
  ctx.drawImage(video, dx, dy, dw, dh)
}

/** 腾讯云渲染绿幕：G 明显高于 R/B 的像素变透明，并压掉边缘溢色 */
function keyGreen(data) {
  const hard = 62
  const soft = 48
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]
    const maxRB = r > b ? r : b
    const excess = g - maxRB

    if (excess <= 10) {
      if (excess > 0 && g > r && g > b) {
        data[i + 1] = maxRB + excess * 0.3
      }
      continue
    }

    if (excess >= hard) {
      data[i + 3] = 0
      continue
    }

    const t = (excess - (hard - soft)) / soft
    if (t <= 0) {
      data[i + 1] = g - excess * 0.45
      continue
    }
    const k = t > 1 ? 1 : t
    data[i + 3] = Math.round(data[i + 3] * (1 - k))
    data[i + 1] = Math.round(g - excess * 0.7 * k)
  }
}

/**
 * @param {HTMLElement} container
 * @param {{ objectPositionX?: number }} [options]
 */
export function attachChromaKey(container, options = {}) {
  if (!container) return null

  ensureSvgFilter()

  const objectPositionX = options.objectPositionX ?? 0.5
  const canvas = document.createElement('canvas')
  canvas.className = CANVAS_CLASS
  canvas.setAttribute('aria-hidden', 'true')
  canvas.style.cssText = [
    'position:absolute',
    'inset:0',
    'width:100%',
    'height:100%',
    'pointer-events:none',
    'z-index:3',
    'display:none',
  ].join(';')
  container.appendChild(canvas)

  const ctx = canvas.getContext('2d', { willReadFrequently: true, alpha: true })

  const state = {
    video: null,
    raf: 0,
    mode: '',
    stopped: false,
    probeFails: 0,
  }

  function setMode(mode) {
    if (state.mode === mode) return
    state.mode = mode
    container.classList.toggle('dh-chroma-on', mode === 'canvas')
    container.classList.toggle('dh-chroma-svg', mode === 'svg')
    canvas.style.display = mode === 'canvas' ? 'block' : 'none'
  }

  function resizeCanvas() {
    const rect = container.getBoundingClientRect()
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const maxW = 720
    const cssW = Math.max(2, rect.width)
    const scale = cssW > maxW ? maxW / cssW : dpr
    const w = Math.max(2, Math.round(cssW * scale))
    const h = Math.max(2, Math.round(Math.max(2, rect.height) * scale))
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w
      canvas.height = h
    }
  }

  function useSvgFallback() {
    setMode('svg')
    const video = state.video
    if (video) video.style.filter = `url(#${FILTER_ID})`
  }

  function tick() {
    if (state.stopped) return
    state.raf = requestAnimationFrame(tick)

    if (document.visibilityState === 'hidden') return
    const video = state.video
    if (!video || !video.videoWidth) return

    if (state.mode === 'svg') return

    resizeCanvas()
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    drawVideoCover(ctx, video, canvas.width, canvas.height, objectPositionX)

    try {
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height)
      keyGreen(img.data)
      ctx.putImageData(img, 0, 0)
      state.probeFails = 0
      setMode('canvas')
    } catch (_) {
      state.probeFails += 1
      if (state.probeFails >= 2) useSvgFallback()
    }
  }

  function bindVideo(video) {
    if (state.video === video) return
    if (state.video) state.video.style.filter = ''
    state.video = video
    state.probeFails = 0
    state.mode = ''
    if (video) {
      const start = () => {
        if (!state.raf && !state.stopped) tick()
      }
      if (video.readyState >= 2) start()
      else video.addEventListener('loadeddata', start, { once: true })
      video.addEventListener('play', start, { once: true })
    }
  }

  function refresh() {
    if (state.stopped) return
    bindVideo(findVideo(container))
    if (!state.raf && state.video) tick()
  }

  function destroy() {
    state.stopped = true
    if (state.raf) cancelAnimationFrame(state.raf)
    state.raf = 0
    if (state.video) state.video.style.filter = ''
    container.classList.remove('dh-chroma-on', 'dh-chroma-svg')
    if (canvas.parentNode) canvas.parentNode.removeChild(canvas)
    state.video = null
  }

  refresh()

  return { refresh, destroy }
}
